import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import type { RootState } from "../../common/state/store";
import {
  duplicateProjectGroup,
  getEditedProjectGroup,
  getNewProjectGroup,
  getSelectedProjectGroup,
  setCurrentElementsFromCurrentProjectGroup,
  setEditedThumbnail,
  setNewProjectGroup,
  updateProjectGroup,
} from "../../features/projectGroups/state/projectGroupsSlice";
import { DynamicMedia } from "../../features/graph/components/DynamicField";
import { setMediaSearch, setOriginalMediaGallery } from "../../features/gallery/state/gallerySlice";
import { useMediaById } from "../../features/gallery/useMediaById";
import { useEffect, useState } from "react";
import type { IMedia } from "../../model/unityObject";
import { closeStandardModal, openStandardModal } from "../../common/state/slice/modal/modalSlice";
import ModalGallery from "../../features/graph/components/ModalGallery";
import { FooterButtons } from "../../common/components/FooterButtons";
import { PROJECT_GROUP_LIST_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { IsLoading, useLoading } from "src/common/components/AppState/IsLoading";
import { LoadingState } from "src/common/state/slice/appStateSlice";
import { useTourView } from "src/features/onboarding/engine/useTourView";
import { TOUR_VIEW_ANCHORS } from "src/features/onboarding/engine/tourViewAnchors";
import { trackEvent } from "../../trackEvent";

interface ICreateProjectGroupThumbnail {
  incrementStep?: () => void;
  decrementStep?: () => void;
  mode?: "new" | "edit" | "duplicate";
}

const DEFAULT_THUMBNAIL_ID = 47;

const CreateProjectGroupThumbnail = ({
  incrementStep,
  decrementStep,
  mode,
}: ICreateProjectGroupThumbnail) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const projectGroupListLoadingState = useLoading(PROJECT_GROUP_LIST_COMPONENT_ID);
  useTourView(
    "course-wizard-thumbnail",
    projectGroupListLoadingState !== LoadingState.Loading,
    TOUR_VIEW_ANCHORS["course-wizard-thumbnail"],
  );

  const { register, handleSubmit, setValue, control } = useForm();

  const { newProjectGroup, selectedProjectGroup, editedProjectGroup } = useAppSelector(
    (state: RootState) => {
      return {
        newProjectGroup: getNewProjectGroup(state),
        selectedProjectGroup: getSelectedProjectGroup(state),
        editedProjectGroup: getEditedProjectGroup(state),
      };
    },
  );

  const initialThumbnailId = (): number => {
    if (mode === "edit") {
      return Number(editedProjectGroup?.source_thumbnail?.id) || DEFAULT_THUMBNAIL_ID;
    }
    return Number(newProjectGroup.thumbnailId) || DEFAULT_THUMBNAIL_ID;
  };

  const [currentThumbnailId, setCurrentThumbnailId] = useState<number>(initialThumbnailId);
  const {
    media: currentThumbnailMedia,
    status: currentThumbnailStatus,
    retry: retryCurrentThumbnail,
  } = useMediaById(currentThumbnailId > 0 ? currentThumbnailId : undefined);
  const isThumbnailLoading =
    currentThumbnailId > 0 && !currentThumbnailMedia && currentThumbnailStatus === "loading";
  const isThumbnailLoadError = !currentThumbnailMedia && currentThumbnailStatus === "error";

  useEffect(() => {
    dispatch(closeStandardModal("modal-gallery"));
  }, []);

  // Dance around the media preview issues that arose from repurposing the "EditNodeForm" component
  const getEnhancedPgSourceThumbnailField = () => {
    const enhancedField = {
      type: "media",
      mediaType: "image",
      fieldName: "source_thumbnail",
      currentValue: currentThumbnailId,
      relevantSourceMedia: currentThumbnailMedia,
    };
    if (!currentThumbnailId) {
      enhancedField.currentValue = -1;
      enhancedField.relevantSourceMedia = currentThumbnailMedia;
    }
    return enhancedField;
  };

  const openGallery = () => {
    dispatch(
      setMediaSearch({
        mediaType: "image",
        media360tag: "",
      }),
    );
    dispatch(setOriginalMediaGallery(currentThumbnailId));
    dispatch(openStandardModal("modal-gallery"));
  };

  const onSubmit = () => {};

  // Callback from ModalGallery selection
  const onMediaChange = (selectedMedia: IMedia) => {
    dispatch(closeStandardModal("modal-gallery"));
    setCurrentThumbnailId(Number(selectedMedia.id));
  };

  useEffect(() => {
    let tbnId: number;
    switch (mode) {
      case "edit":
        tbnId = Number(editedProjectGroup.source_thumbnail.id);
        setValue("thumbnailId", tbnId);
        setCurrentThumbnailId(tbnId);
        break;
      case "duplicate":
        tbnId = newProjectGroup.thumbnailId;
        setValue("thumbnailId", tbnId);
        setCurrentThumbnailId(tbnId);
        break;
      case "new":
      default:
        tbnId = newProjectGroup.thumbnailId;
        setValue("thumbnailId", tbnId);
        setCurrentThumbnailId(tbnId);
        break;
    }
  }, [editedProjectGroup.source_thumbnail]);

  const enhancedField = getEnhancedPgSourceThumbnailField();

  const handleSave = () => {
    if (mode === "edit") {
      void dispatch(
        updateProjectGroup({
          id: selectedProjectGroup.id,
          payload: {
            source_thumbnail: enhancedField.relevantSourceMedia,
          },
          componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(setAlert({ msg: t("alerts.projectGroupUpdateSuccess"), type: "success" }));
          dispatch(setEditedThumbnail(enhancedField.relevantSourceMedia as IMedia));
        });
      dispatch(setCurrentElementsFromCurrentProjectGroup());
      navigate(`/courses/${selectedProjectGroup.id}/list`);
    } else if (mode === "duplicate") {
      trackEvent("pg-duplicate");
      const duplicatedName = newProjectGroup.newName;
      navigate("/courses");
      dispatch(
        setAlert({
          type: "success",
          msg: t("alerts.projectGroupDuplicationStarted"),
          subMsg: t("alerts.projectGroupDuplicationStartedSubMsg", { name: duplicatedName }),
          customTimeoutInMs: 30000,
        }),
      );
      dispatch(
        duplicateProjectGroup({
          projectGroup: selectedProjectGroup.id,
          thumbnail: currentThumbnailId,
          newName: duplicatedName,
        }),
      )
        .unwrap()
        .then((res) => {
          if (!res?.data?.duplicatedProjectGroup?.id) {
            dispatch(
              setAlert({
                type: "danger",
                msg: t("alerts.projectGroupDuplicationFail", { name: duplicatedName }),
                customTimeoutInMs: 30000,
              }),
            );
            return;
          }
          dispatch(
            setAlert({
              type: "success",
              msg: t("alerts.projectGroupDuplicationSuccess", { name: duplicatedName }),
              subMsg: t("alerts.projectGroupDuplicationSuccessSubMsg"),
              customTimeoutInMs: 30000,
            }),
          );
        })
        .catch(() => {
          dispatch(
            setAlert({
              type: "danger",
              msg: t("alerts.projectGroupDuplicationFail", { name: duplicatedName }),
              customTimeoutInMs: 30000,
            }),
          );
        });
    }
  };

  const handleCancel = () => {
    navigate(`/courses/${selectedProjectGroup.id}/list`);
  };

  return (
    <>
      <IsLoading
        componentId={PROJECT_GROUP_LIST_COMPONENT_ID}
        showSuccess={false}
        spinnerPlaceholder
      >
        <h1 className="text-2xl my-5 px-6 font-medium">{t("general.selectThumbnail")}</h1>
        <ModalGallery
          nodeId={"new-project-group-tbn"}
          onMediaChange={onMediaChange}
          allowedMediaTypeOverride="image"
        />

        <form
          className="flex flex-col grow w-full justify-between"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mb-10 w-full my-5 px-6">
            <div
              className="flex flex-col items-center justify-center bg-green-200 p-6 w-full max-w-max"
              data-tour="course-wizard-thumbnail"
            >
              <Controller
                control={control}
                name={"source_thumbnail"}
                render={({ field }) => (
                  <DynamicMedia
                    field={field}
                    fieldType={enhancedField.type}
                    register={register}
                    handleButtonClick={() => {
                      openGallery();
                    }}
                    currentMedia={enhancedField.currentValue}
                    media={enhancedField.relevantSourceMedia as IMedia}
                    isLoading={isThumbnailLoading}
                    loadError={isThumbnailLoadError}
                    onRetryLoad={retryCurrentThumbnail}
                  />
                )}
              />
            </div>
          </div>
          {mode === "edit" || mode === "duplicate" ? (
            <FooterButtons
              cancelCb={handleCancel}
              cancelText={t("general.cancel")}
              nextText={t("general.save")}
              nextCb={handleSave}
            />
          ) : (
            <FooterButtons
              cancelText={t("general.cancel")}
              cancelCb={() => navigate("/courses")}
              backText={t("general.back")}
              backCb={decrementStep}
              nextText={t("general.next")}
              nextCb={() => {
                dispatch(
                  setNewProjectGroup({ ...newProjectGroup, thumbnailId: currentThumbnailId }),
                );

                if (incrementStep) {
                  incrementStep();
                }
              }}
              nextDisabled={!enhancedField.relevantSourceMedia}
            />
          )}
        </form>
      </IsLoading>
    </>
  );
};

export default CreateProjectGroupThumbnail;
